import {
  AvatarFallback,
  CustomSidebarMenuButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { Avatar } from '@radix-ui/react-avatar';
import {
  PiCaretUpDownThin,
  PiCheck,
  PiClock,
  PiGear,
  PiHourglass,
  PiQuestion,
  PiSignOut,
  PiTag,
  PiUsersThreeThin,
  PiWarning,
  PiX,
} from 'react-icons/pi';
import { useHelp } from '@ais-ui/help';
import React, { useEffect, useMemo } from 'react';
import { useSettings } from '../../modules/Settings/SettingsProvider';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../routes';
import { useAuth } from '@ais-ui/auth';
import { useProfile } from '@ais-ui/profile';
import { useReleaseNotesContext } from '../../modules/ReleaseNotes/ReleaseNotesProvider';
import { useVersion } from './useVersion';
import { AsyncJob, AsyncJobStatus, useAsyncJobs } from '@ais-ui/async-jobs';
import { Loader2 } from 'lucide-react';
import { cn } from '@ais-ui/utils';

interface UserDropdownProps {
  expanded: boolean;
}

interface UserDropdownButtonProps {
  expanded: boolean;
}

const UserDropDownButton = React.forwardRef<
  HTMLButtonElement,
  UserDropdownButtonProps
>(({ expanded, ...props }, ref) => {
  const { email, username, shortUsername } = useProfile();
  const { version } = useVersion();

  if (expanded) {
    return (
      <CustomSidebarMenuButton
        ref={ref}
        {...props}
        size="lg"
        id={'user-menu-button'}
        className="text-sidebar-accent-foreground hover:bg-secondary rounded-lg"
      >
        <Avatar className="h-8 w-8 rounded-lg relative">
          <AvatarFallback className="rounded-lg bg-blue-100">
            {shortUsername}
          </AvatarFallback>
        </Avatar>
        <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
          <div className="flex items-center min-w-0 justify-between">
            <span className="truncate font-semibold mr-2 min-w-0">
              {username}
            </span>
            <span className="flex-shrink-0 text-xs whitespace-nowrap">
              v{version}
            </span>
          </div>
          <span className="truncate text-xs">{email}</span>
        </div>
        <PiCaretUpDownThin className="ml-auto h-6 w-6 flex-shrink-0" />
      </CustomSidebarMenuButton>
    );
  }
  return (
    <CustomSidebarMenuButton
      ref={ref}
      {...props}
      size="lg"
      className="flex justify-center bg-sidebar-accent text-sidebar-accent-foreground hover:bg-secondary rounded-lg w-full"
    >
      <div className="relative h-8 w-8">
        <Avatar className="h-8 w-8 rounded-lg">
          <AvatarFallback className="rounded-lg bg-secondary bg-blue-100">
            {shortUsername}
          </AvatarFallback>
        </Avatar>
      </div>
    </CustomSidebarMenuButton>
  );
});

const UserDropdownHeader = () => {
  const { email, username, shortUsername, groups } = useProfile();
  const { version } = useVersion();
  return (
    <DropdownMenuLabel className="p-0 font-normal">
      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
        <Avatar className="h-8 w-8 rounded-lg">
          <AvatarFallback className="rounded-lg bg-blue-100">
            {shortUsername}
          </AvatarFallback>
        </Avatar>
        <div className="grid flex-1 text-left text-sm leading-tight">
          <div className="flex justify-between space-x-4">
            <div className={'flex space-x-2'}>
              <span className="truncate font-semibold">{username}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <PiUsersThreeThin className="h-4 w-4 mb-1"></PiUsersThreeThin>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="">
                      <h2 className="text-sm font-semibold">Groups</h2>
                      <Separator className="mt-1 mb-2"></Separator>
                      {groups.length > 0 ? (
                        <ul className="space-y-1">
                          {groups.map((group, index) => (
                            <li className={'text-sm'} key={index}>
                              {group}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={'text-sm'}>Not member of any groups</p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <span className="text-xs">v{version}</span>
          </div>
          <span className="truncate text-xs">{email}</span>
        </div>
      </div>
    </DropdownMenuLabel>
  );
};

const AsyncJobs = () => {
  const { jobs, cancelJob, clearCompletedJobs } = useAsyncJobs();
  const [open, setOpen] = React.useState(false);

  const anyRunningJobs = useMemo(() => {
    return jobs.some((job: AsyncJob) => job.status === 'running');
  }, [jobs]);

  // Function to calculate job duration
  const calculateDuration = (job: AsyncJob) => {
    if (!job.startedAt) return 'Not started';

    const startTime = new Date(job.startedAt);
    const endTime = job.completedAt ? new Date(job.completedAt) : new Date();

    const durationMs = endTime.getTime() - startTime.getTime();

    // Format duration nicely
    if (durationMs < 1000) {
      return `${durationMs}ms`;
    } else if (durationMs < 60000) {
      return `${Math.floor(durationMs / 1000)}s`;
    } else {
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  };

  // Function to get job status icon
  const getStatusIcon = (status: AsyncJobStatus) => {
    switch (status) {
      case 'completed':
        return <PiCheck className="h-3 w-3 text-green-500" />;
      case 'running':
        return (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        );
      case 'failed':
        return <PiWarning className="h-3 w-3 text-red-500" />;
      default:
        return <PiClock className="h-3 w-3 text-muted-foreground" />;
    }
  };

  const formatJobName = (job: AsyncJob) => {
    return job.name || `Job ${job.id.substring(0, 8)}`;
  };

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <DropdownMenuItem
          id="sidebar-jobs-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
          className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
        >
          <PiHourglass
            className={cn('h-4 w-4', anyRunningJobs && 'animate-spin')}
          />
          <p>Jobs {jobs.length > 0 && `(${jobs.length})`}</p>
        </DropdownMenuItem>
      </PopoverTrigger>
      <PopoverContent side="right" className="ml-2" align="end">
        <div className="flex flex-col gap-2">
          {jobs.length > 0 && (
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">Jobs ({jobs.length})</h3>
              <button
                onClick={() => clearCompletedJobs()}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear completed
              </button>
            </div>
          )}

          {jobs.map((job) => (
            <div
              key={job.id}
              className="mb-2 rounded-md p-2 hover:bg-muted/50 border border-muted"
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {formatJobName(job)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Duration: {calculateDuration(job)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {getStatusIcon(job.status)}
                  {job.status !== 'completed' && job.status !== 'failed' && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        cancelJob(job.id);
                      }}
                      className="ml-1 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <PiX className="h-3 w-3" />
                      <span className="sr-only">Cancel</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {jobs.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-4">
              No active jobs
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const UserDropdown = ({ expanded }: UserDropdownProps) => {
  const { saveMapState } = useSettings();
  const { logout } = useAuth();
  const { setOpen: setOpenHelp } = useHelp();
  const { setOpen: setOpenReleaseNotes } = useReleaseNotesContext();
  const navigate = useNavigate();

  const handleSettingsClick = () => {
    saveMapState();
    navigate(ROUTES.SETTINGS.path);
  };

  const handleLogoutClick = () => {
    logout('');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <UserDropDownButton expanded={expanded} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-80 rounded-lg"
        side={'right'}
        align="end"
        sideOffset={4}
      >
        <UserDropdownHeader></UserDropdownHeader>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            id="sidebar-help-button"
            onClick={() => setOpenHelp(true)}
            className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
          >
            <PiQuestion className="h-4 w-4"></PiQuestion>
            <p>Help</p>
          </DropdownMenuItem>
          <DropdownMenuItem
            id="sidebar-release-notes-button"
            onClick={() => setOpenReleaseNotes(true)}
            className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
          >
            <PiTag className="h-4 w-4" />
            <p>Release notes</p>
          </DropdownMenuItem>
          <DropdownMenuItem
            id="sidebar-settings-button"
            onClick={handleSettingsClick}
            className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
          >
            <PiGear className="h-4 w-4" />
            <p>Settings</p>
          </DropdownMenuItem>
          <AsyncJobs></AsyncJobs>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          id="sidebar-logout-button"
          onClick={handleLogoutClick}
          className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
        >
          <PiSignOut className="h-4 w-4" />
          <p className="">Log out</p>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
